import { DateTime } from 'luxon';
import { logEntryHoldsUserData } from '@cf-shared/data-model/log-entry-user-data';
import { Log_Entry } from '@cf-shared/data-model/user-log';

/**
 * What to report when a day's once-a-day collapse set a document aside, or nothing when there is
 * nothing to report.
 *
 * `reportKeys` holds one key per document set aside — the caller remembers them so the same
 * document is never reported twice; `isUnresolvedProblem` picks the telemetry tier; `name` and
 * `context` are passed straight to the logging service.
 */
export interface DroppedSingletonReport {
  reportKeys: string[];
  isUnresolvedProblem: boolean;
  name: string;
  context: {
    day: string;
    droppedCount: number;
    droppedIds: string;
    droppedTypes: string;
  };
}

/** A document the user had put something in was set aside — still unresolved, so problem tier. */
export const DUPLICATE_DAY_EVENT_SLOT = 'duplicate-day-event-slot';

/** Only an empty placeholder was set aside — nothing was lost, so the quieter info tier. */
export const DUPLICATE_EMPTY_DAY_EVENT_SLOT = 'duplicate-empty-day-event-slot';

/**
 * Describe the report for the documents `pickOneEntryPerSingletonSlot` left out of a day.
 *
 * Every entry type except a meal happens once a day, so a day should hold one document for each.
 * It can hold two: an entry holding something the user did is never deleted, so a reschedule or a
 * second device can leave a completed entry beside an empty placeholder. The collapse picks one
 * and sets the other aside. Nothing is deleted by that — the other document stays in the database
 * — but until now nothing said it had happened, so we could not tell from production whether a
 * real user's day ever holds two (the open question behind #4511).
 *
 * Two very different things reach here and they must not be reported at the same tier, exactly as
 * for the sibling meal rule (`reportDroppedDuplicateMeals` in `user-db.service.ts`). The collapse
 * prefers a document holding user data over an empty placeholder, so the ordinary case sets aside
 * a leftover placeholder that held nothing and cost nobody anything — a completed repair, which
 * goes to the info tier. Only a document holding something the user entered is an unresolved
 * problem, because the screens applying this rule then show none of what it holds.
 *
 * Whether a document holds user data is asked through `logEntryHoldsUserData` and nothing else.
 * That reads a workout's CONTENT rather than its state field, so a finished session stored as
 * `Scheduled` still counts as a real loss (#4840) — reading the field alone is what hid a
 * finished session behind an empty placeholder in the first place.
 *
 * Each set-aside document gets its OWN key — the day plus that document's id — rather than the
 * whole set sharing one. A key per document is what makes the answer the same for every screen:
 * the callers do not all look at the same entries, so the sets they set aside differ. The day list
 * in its workout-only mode has already filtered its entries down to workouts, while the day's meal
 * read passes every type, so one day can produce a set and a subset of it. Keyed on the set, those
 * are two different keys and the day reports twice; keyed per document, the subset is already
 * accounted for and stays quiet. A genuinely NEW duplicate still reports, because its id has not
 * been seen.
 *
 * See design-docs/telemetry.md, "Three telemetry severity tiers", and
 * design-docs/sync-integrity.md, "One Rule for Which Day an Entry Belongs To, and One Meal per
 * Slot".
 */
export function describeDroppedSingletonEntries(
  dayReferenceTime: DateTime,
  dropped: Log_Entry[],
): DroppedSingletonReport | undefined {
  // if nothing was set aside, there is nothing to report
  if (dropped.length === 0) return undefined;

  const day = dayReferenceTime.toISODate();
  const droppedIds = dropped.map(e => e._id || '');
  const isUnresolvedProblem = dropped.some(logEntryHoldsUserData);

  return {
    reportKeys: droppedIds.map(id => `${day}:${id}`),
    isUnresolvedProblem,
    name: isUnresolvedProblem ? DUPLICATE_DAY_EVENT_SLOT : DUPLICATE_EMPTY_DAY_EVENT_SLOT,
    context: {
      day,
      droppedCount: dropped.length,
      droppedIds: droppedIds.join(','),
      droppedTypes: dropped.map(e => String(e.type)).join(','),
    },
  };
}
